import { useState, useEffect, useMemo } from "react";
import { useApi } from "@/lib/useApi";
import { api, apiErr } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/common";
import { Repeat, Wand2, Plus, Trash2, CalendarDays, Sparkles, Play, ChevronLeft, ChevronRight, Save } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];
const OFF = "off";

function mondayOf(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // 0 = mån
  x.setDate(x.getDate() - day);
  x.setHours(12, 0, 0, 0);
  return x;
}
function toISODate(d) { return mondayOf(d).toISOString().slice(0, 10); }
function fmtDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("sv-SE", { day: "numeric", month: "short" });
}
function emptyWeeks(n) { return Array.from({ length: n }, () => Array(7).fill("")); }

// ---------- cellväljare ----------
function CellSelect({ value, shifts, onChange, testid }) {
  return (
    <Select value={value || OFF} onValueChange={(v) => onChange(v === OFF ? "" : v)}>
      <SelectTrigger className="h-8 text-xs" data-testid={testid}><SelectValue /></SelectTrigger>
      <SelectContent>
        <SelectItem value={OFF}>Ledig</SelectItem>
        {(shifts || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

// ---------- mönster-editor ----------
function PatternEditor({ shifts, initial, onSaved, onCancel }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [cycle, setCycle] = useState(initial?.cycle_weeks || 2);
  const [weeks, setWeeks] = useState(initial?.weeks || emptyWeeks(initial?.cycle_weeks || 2));

  useEffect(() => {
    setWeeks((prev) => {
      const next = emptyWeeks(cycle);
      for (let w = 0; w < cycle; w++) if (prev[w]) next[w] = prev[w];
      return next;
    });
  }, [cycle]);

  const setCell = (w, d, val) => setWeeks((prev) => {
    const copy = prev.map((row) => [...row]);
    copy[w][d] = val;
    return copy;
  });

  const save = async () => {
    if (!name.trim()) return toast.error("Ange ett namn");
    const body = { name, description, cycle_weeks: cycle, weeks, template_key: initial?.template_key || null };
    try {
      if (initial?.id) await api.patch(`/shift-patterns/${initial.id}`, body);
      else await api.post("/shift-patterns", body);
      toast.success("Mönster sparat");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Card className="space-y-4 p-4" data-testid="pattern-editor">
      <div className="grid gap-3 sm:grid-cols-3">
        <div><Label>Namn</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="t.ex. 3-skift roterande" data-testid="pattern-name" /></div>
        <div><Label>Cykel (veckor)</Label>
          <Select value={String(cycle)} onValueChange={(v) => setCycle(Number(v))}>
            <SelectTrigger data-testid="pattern-cycle"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3, 4, 5, 6, 7, 8].map((n) => <SelectItem key={n} value={String(n)}>{n} v</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label>Beskrivning</Label><Input value={description} onChange={(e) => setDescription(e.target.value)} data-testid="pattern-desc" /></div>
      </div>
      {(shifts || []).length === 0 &&
        <p className="rounded-md bg-amber-50 p-2 text-xs text-amber-700">Skapa skift under Organisation → Skift först, så du kan placera dem i mönstret.</p>}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          <thead>
            <tr><th className="p-1 text-left text-xs text-muted-foreground">Vecka</th>
              {DAYS.map((d) => <th key={d} className="p-1 text-xs font-medium text-muted-foreground">{d}</th>)}</tr>
          </thead>
          <tbody>
            {weeks.map((row, w) => (
              <tr key={w}>
                <td className="p-1 text-xs font-semibold">V{w + 1}</td>
                {row.map((cell, d) => (
                  <td key={d} className="p-1"><CellSelect value={cell} shifts={shifts} onChange={(v) => setCell(w, d, v)} testid={`cell-${w}-${d}`} /></td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex gap-2">
        <Button onClick={save} data-testid="save-pattern"><Save className="mr-1 h-4 w-4" />Spara mönster</Button>
        {onCancel && <Button variant="outline" onClick={onCancel}>Avbryt</Button>}
      </div>
    </Card>
  );
}

// ---------- roll → skift-mappning (för mallar & AI-förslag) ----------
function applyRoleMapping(proposalWeeks, roleMap) {
  return proposalWeeks.map((row) => row.map((code) => (code === OFF || !code) ? "" : (roleMap[code] || "")));
}

function RoleMapper({ roles, roleLabels, shifts, roleMap, setRoleMap }) {
  return (
    <div className="grid gap-2 sm:grid-cols-3">
      {roles.filter((r) => r !== OFF).map((r) => (
        <div key={r}>
          <Label className="text-xs">{roleLabels?.[r] || r} → skift</Label>
          <Select value={roleMap[r] || ""} onValueChange={(v) => setRoleMap((m) => ({ ...m, [r]: v }))}>
            <SelectTrigger data-testid={`rolemap-${r}`}><SelectValue placeholder="Välj skift" /></SelectTrigger>
            <SelectContent>{(shifts || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      ))}
    </div>
  );
}

export default function ShiftPatterns() {
  const { data: shifts } = useApi("/shifts");
  const { data: teams, reload: reloadTeams } = useApi("/teams");
  const { data: patterns, reload: reloadPatterns } = useApi("/shift-patterns");
  const { data: company, reload: reloadCompany } = useApi("/company");

  const [templates, setTemplates] = useState([]);
  const [roleLabels, setRoleLabels] = useState({});
  const [editing, setEditing] = useState(null); // {id?, ...} pattern being edited
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    api.get("/shift-patterns/templates").then((r) => {
      setTemplates(r.data.templates || []);
      setRoleLabels(r.data.role_labels || {});
    }).catch(() => {});
  }, []);

  const startNew = (base) => { setEditing(base || null); setShowEditor(true); };

  return (
    <div>
      <PageHeader title="Skiftmönster" icon={Repeat}
        subtitle="Bygg roterande scheman – 2- och 3-skift, varannan vecka dag/kväll, 7 på/7 av m.m. Låt Skiftguiden föreslå ett upplägg utifrån er produktion." />

      <Tabs defaultValue="guide">
        <TabsList>
          <TabsTrigger value="guide" data-testid="tab-guide"><Sparkles className="mr-1 h-4 w-4" />Skiftguiden</TabsTrigger>
          <TabsTrigger value="patterns" data-testid="tab-patterns"><Repeat className="mr-1 h-4 w-4" />Mönster</TabsTrigger>
          <TabsTrigger value="assign" data-testid="tab-assign"><CalendarDays className="mr-1 h-4 w-4" />Koppla team</TabsTrigger>
          <TabsTrigger value="week" data-testid="tab-week"><CalendarDays className="mr-1 h-4 w-4" />Veckovy</TabsTrigger>
        </TabsList>

        {/* ---------- AI-GUIDE ---------- */}
        <TabsContent value="guide" className="mt-4">
          <ShiftGuide shifts={shifts} roleLabels={roleLabels}
            onUse={(pattern) => { startNew(pattern); }} />
        </TabsContent>

        {/* ---------- MÖNSTER ---------- */}
        <TabsContent value="patterns" className="mt-4 space-y-4">
          {!showEditor && (
            <Card className="flex flex-wrap items-center gap-2 p-4">
              <Button onClick={() => startNew(null)} data-testid="new-pattern"><Plus className="mr-1 h-4 w-4" />Nytt mönster</Button>
              <span className="text-xs text-muted-foreground">eller börja från en mall:</span>
              <TemplatePicker templates={templates} roleLabels={roleLabels} shifts={shifts}
                onPick={(p) => startNew(p)} />
            </Card>
          )}
          {showEditor &&
            <PatternEditor shifts={shifts} initial={editing}
              onSaved={() => { setShowEditor(false); reloadPatterns(); }}
              onCancel={() => setShowEditor(false)} />}

          {(patterns || []).length === 0 && !showEditor ?
            <EmptyState icon={Repeat} title="Inga skiftmönster ännu" text="Skapa ett mönster manuellt, från en mall eller med Skiftguiden." /> :
            <div className="grid gap-2 md:grid-cols-2">
              {(patterns || []).map((p) => (
                <Card key={p.id} className="p-4" data-testid={`pattern-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="font-medium">{p.name}</div>
                      <div className="text-xs text-muted-foreground">{p.description}</div>
                      <Badge variant="secondary" className="mt-1">Cykel: {p.cycle_weeks} v</Badge>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => startNew(p)} data-testid={`edit-${p.id}`}>Redigera</Button>
                      <Button size="sm" variant="ghost" onClick={async () => {
                        await api.delete(`/shift-patterns/${p.id}`); reloadPatterns(); reloadTeams(); toast.success("Borttaget");
                      }} data-testid={`del-${p.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                    </div>
                  </div>
                  <PatternPreview pattern={p} shifts={shifts} />
                </Card>
              ))}
            </div>}
        </TabsContent>

        {/* ---------- KOPPLA TEAM ---------- */}
        <TabsContent value="assign" className="mt-4 space-y-4">
          <GenerateCard company={company} teams={teams} onModeSaved={reloadCompany} />
          <AssignTeams teams={teams} patterns={patterns} onSaved={reloadTeams} />
        </TabsContent>

        {/* ---------- VECKOVY ---------- */}
        <TabsContent value="week" className="mt-4">
          <WeekView />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// mall → editor
function TemplatePicker({ templates, roleLabels, shifts, onPick }) {
  const [sel, setSel] = useState(null);
  const [roleMap, setRoleMap] = useState({});
  const t = templates.find((x) => x.key === sel);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <Select value={sel || ""} onValueChange={(v) => { setSel(v); setRoleMap({}); }}>
        <SelectTrigger className="w-64" data-testid="template-select"><SelectValue placeholder="Välj mall" /></SelectTrigger>
        <SelectContent>{templates.map((x) => <SelectItem key={x.key} value={x.key}>{x.name}</SelectItem>)}</SelectContent>
      </Select>
      {t && (
        <div className="flex w-full flex-col gap-2 rounded-md border p-3">
          <p className="text-xs text-muted-foreground">{t.description}</p>
          <RoleMapper roles={t.roles} roleLabels={roleLabels} shifts={shifts} roleMap={roleMap} setRoleMap={setRoleMap} />
          <Button size="sm" className="self-start" data-testid="use-template"
            disabled={t.roles.some((r) => r !== OFF && !roleMap[r])}
            onClick={() => onPick({ name: t.name, description: t.description, cycle_weeks: t.cycle_weeks,
              template_key: t.key, weeks: applyRoleMapping(t.weeks, roleMap) })}>
            Använd mall
          </Button>
        </div>
      )}
    </div>
  );
}

function PatternPreview({ pattern, shifts }) {
  const name = (id) => (shifts || []).find((s) => s.id === id)?.name || "";
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[520px] border-collapse text-[11px]">
        <thead><tr><th className="p-1 text-left text-muted-foreground">V</th>{DAYS.map((d) => <th key={d} className="p-1 text-muted-foreground">{d}</th>)}</tr></thead>
        <tbody>
          {pattern.weeks.map((row, w) => (
            <tr key={w}><td className="p-1 font-semibold">{w + 1}</td>
              {row.map((c, d) => (
                <td key={d} className="p-1 text-center">
                  {c ? <span className="rounded bg-accent/10 px-1 text-accent">{name(c)}</span> : <span className="text-muted-foreground/50">–</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- Skiftguiden (AI) ----------
function ShiftGuide({ shifts, roleLabels, onUse }) {
  const [form, setForm] = useState({ production_hours: "", around_clock: false, weekend_work: false, num_teams: "", core_mission: "", notes: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [roleMap, setRoleMap] = useState({});

  const run = async () => {
    setLoading(true); setResult(null);
    try {
      const r = await api.post("/shift-guide", { ...form, num_teams: form.num_teams ? Number(form.num_teams) : null });
      setResult(r.data); setRoleMap({});
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  const p = result?.proposal;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="space-y-3 p-4">
        <div><Label>Produktionstider</Label><Input placeholder="t.ex. 06–22" value={form.production_hours} onChange={(e) => setForm((f) => ({ ...f, production_hours: e.target.value }))} data-testid="guide-hours" /></div>
        <div className="flex items-center justify-between rounded-md border p-3">
          <div><Label>Dygnet runt-drift</Label><p className="text-xs text-muted-foreground">Produktion även på natten</p></div>
          <Switch checked={form.around_clock} onCheckedChange={(v) => setForm((f) => ({ ...f, around_clock: v }))} data-testid="guide-24" />
        </div>
        <div className="flex items-center justify-between rounded-md border p-3">
          <div><Label>Helgarbete</Label><p className="text-xs text-muted-foreground">Arbete även lör/sön</p></div>
          <Switch checked={form.weekend_work} onCheckedChange={(v) => setForm((f) => ({ ...f, weekend_work: v }))} data-testid="guide-weekend" />
        </div>
        <div><Label>Antal team/lag</Label><Input type="number" min="1" value={form.num_teams} onChange={(e) => setForm((f) => ({ ...f, num_teams: e.target.value }))} data-testid="guide-teams" /></div>
        <div><Label>Kärnuppdrag / verksamhet</Label><Input placeholder="t.ex. plock & pack e-handel" value={form.core_mission} onChange={(e) => setForm((f) => ({ ...f, core_mission: e.target.value }))} data-testid="guide-mission" /></div>
        <div><Label>Övrigt att ta hänsyn till</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="guide-notes" /></div>
        <Button onClick={run} disabled={loading} data-testid="run-guide"><Wand2 className="mr-1 h-4 w-4" />{loading ? "Analyserar…" : "Föreslå skiftmönster"}</Button>
      </Card>

      <Card className="p-4">
        {!p ? <div className="grid h-full place-items-center text-center text-sm text-muted-foreground">
          <div><Sparkles className="mx-auto mb-2 h-8 w-8 text-accent/50" />Fyll i uppgifterna och låt Skiftguiden föreslå ett upplägg.</div>
        </div> : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="font-head text-lg font-bold">{p.name}</span>
              <Badge variant={result.generated_by === "ai" ? "default" : "secondary"}>{result.generated_by === "ai" ? "AI-förslag" : "Regelmotor"}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">{p.rationale || p.description}</p>
            <PatternPreviewRoles weeks={p.weeks} roleLabels={roleLabels} />
            <div className="rounded-md border p-3">
              <p className="mb-2 text-xs font-medium">Koppla rollerna till era skift innan du använder förslaget:</p>
              <RoleMapper roles={p.roles || []} roleLabels={roleLabels} shifts={shifts} roleMap={roleMap} setRoleMap={setRoleMap} />
            </div>
            <Button disabled={(p.roles || []).some((r) => r !== OFF && !roleMap[r])} data-testid="use-guide"
              onClick={() => onUse({ name: p.name, description: p.description, cycle_weeks: p.cycle_weeks,
                template_key: null, weeks: applyRoleMapping(p.weeks, roleMap) })}>
              Använd förslag → öppna i editorn
            </Button>
            <p className="text-[11px] text-muted-foreground">{result.disclaimer}</p>
          </div>
        )}
      </Card>
    </div>
  );
}

function PatternPreviewRoles({ weeks, roleLabels }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[520px] border-collapse text-[11px]">
        <thead><tr><th className="p-1 text-left text-muted-foreground">V</th>{DAYS.map((d) => <th key={d} className="p-1 text-muted-foreground">{d}</th>)}</tr></thead>
        <tbody>
          {weeks.map((row, w) => (
            <tr key={w}><td className="p-1 font-semibold">{w + 1}</td>
              {row.map((c, d) => (
                <td key={d} className="p-1 text-center">
                  {c && c !== OFF ? <span className="rounded bg-accent/10 px-1 text-accent">{roleLabels?.[c] || c}</span> : <span className="text-muted-foreground/50">–</span>}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------- koppla team + generering ----------
function AssignTeams({ teams, patterns, onSaved }) {
  const patName = (id) => (patterns || []).find((p) => p.id === id)?.name;
  const save = async (team_id, patch) => {
    try {
      const t = (teams || []).find((x) => x.id === team_id) || {};
      await api.post("/shift-patterns/assign-team", {
        team_id,
        pattern_id: patch.pattern_id !== undefined ? patch.pattern_id : (t.pattern_id || null),
        anchor_monday: patch.anchor_monday !== undefined ? patch.anchor_monday : (t.pattern_anchor_monday || null),
      });
      toast.success("Sparat"); onSaved();
    } catch (e) { toast.error(apiErr(e)); }
  };
  if ((teams || []).length === 0) return <EmptyState icon={CalendarDays} title="Inga team" text="Skapa team under Organisation först." />;
  return (
    <Card className="p-4">
      <div className="mb-2 text-sm font-medium">Koppla ett skiftmönster till varje team</div>
      <div className="space-y-2">
        {(teams || []).map((t) => (
          <div key={t.id} className="flex flex-wrap items-center gap-3 rounded-md border p-3" data-testid={`assign-team-${t.id}`}>
            <div className="min-w-[140px] flex-1 font-medium">{t.name}</div>
            <Select value={t.pattern_id || "none"} onValueChange={(v) => save(t.id, { pattern_id: v === "none" ? null : v })}>
              <SelectTrigger className="w-56" data-testid={`assign-pattern-${t.id}`}><SelectValue placeholder="Inget mönster" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Inget mönster</SelectItem>
                {(patterns || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex items-center gap-1">
              <Label className="text-xs text-muted-foreground">Startvecka (mån)</Label>
              <Input type="date" className="w-40" defaultValue={t.pattern_anchor_monday || ""}
                onBlur={(e) => e.target.value && save(t.id, { anchor_monday: e.target.value })}
                data-testid={`assign-anchor-${t.id}`} />
            </div>
            {t.pattern_id && <Badge variant="secondary">{patName(t.pattern_id)}</Badge>}
          </div>
        ))}
      </div>
    </Card>
  );
}

function GenerateCard({ company, teams, onModeSaved }) {
  const [mode, setMode] = useState(company?.pattern_mode || "manual");
  const [weeks, setWeeks] = useState(4);
  const [teamId, setTeamId] = useState("all");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setMode(company?.pattern_mode || "manual"); }, [company]);

  const saveMode = async (v) => {
    setMode(v);
    try { await api.post("/shift-patterns/mode", { pattern_mode: v }); onModeSaved(); toast.success("Läge sparat"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const generate = async () => {
    setBusy(true);
    try {
      const r = await api.post("/shift-patterns/generate", {
        team_id: teamId === "all" ? null : teamId,
        weeks_ahead: weeks, start_monday: toISODate(new Date()),
      });
      toast.success(`${r.data.created} pass genererade (${r.data.weeks} veckor)`);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-center justify-between rounded-md border p-3">
        <div>
          <Label>Genereringsläge</Label>
          <p className="text-xs text-muted-foreground">Auto: schemat följer mönstret framåt. Manuell: du genererar pass själv vid behov.</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs ${mode === "manual" ? "font-semibold" : "text-muted-foreground"}`}>Manuell</span>
          <Switch checked={mode === "auto"} onCheckedChange={(v) => saveMode(v ? "auto" : "manual")} data-testid="mode-switch" />
          <span className={`text-xs ${mode === "auto" ? "font-semibold" : "text-muted-foreground"}`}>Auto</span>
        </div>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <div><Label className="text-xs">Team</Label>
          <Select value={teamId} onValueChange={setTeamId}>
            <SelectTrigger className="w-52" data-testid="gen-team"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Alla team med mönster</SelectItem>
              {(teams || []).map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select></div>
        <div><Label className="text-xs">Veckor framåt</Label>
          <Input type="number" min="1" max="12" className="w-24" value={weeks} onChange={(e) => setWeeks(Number(e.target.value))} data-testid="gen-weeks" /></div>
        <Button onClick={generate} disabled={busy} data-testid="gen-run"><Play className="mr-1 h-4 w-4" />{busy ? "Genererar…" : "Generera schemapass"}</Button>
      </div>
      <p className="text-[11px] text-muted-foreground">Genererar publicerade pass för teamets medarbetare enligt mönstret. Körs på nytt ersätts tidigare mönster-genererade pass i perioden.</p>
    </Card>
  );
}

// ---------- veckovy ----------
function WeekView() {
  const [monday, setMonday] = useState(mondayOf(new Date()));
  const [data, setData] = useState(null);
  const week = toISODate(monday);

  const load = useMemo(() => async () => {
    try { const r = await api.get(`/shift-schedule?week=${week}`); setData(r.data); }
    catch (e) { toast.error(apiErr(e)); }
  }, [week]);
  useEffect(() => { load(); }, [load]);

  const shift = (n) => setMonday((m) => { const x = new Date(m); x.setDate(x.getDate() + n * 7); return mondayOf(x); });

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-center justify-between">
        <div className="font-medium">Vecka från {fmtDate(week)}</div>
        <div className="flex gap-1">
          <Button size="sm" variant="outline" onClick={() => shift(-1)} data-testid="week-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <Button size="sm" variant="outline" onClick={() => setMonday(mondayOf(new Date()))} data-testid="week-today">Idag</Button>
          <Button size="sm" variant="outline" onClick={() => shift(1)} data-testid="week-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
      </div>
      {(data?.rows || []).length === 0 ? <EmptyState icon={CalendarDays} title="Inga team" text="Skapa team och koppla mönster." /> :
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] border-collapse text-sm" data-testid="week-table">
            <thead>
              <tr><th className="p-2 text-left text-xs text-muted-foreground">Team</th>
                {(data.rows.find((r) => r.days.length)?.days || DAYS.map((d, i) => ({ date: null }))).map((d, i) => (
                  <th key={i} className="p-2 text-xs text-muted-foreground">{DAYS[i]}{d.date ? <div className="font-normal">{fmtDate(d.date)}</div> : null}</th>
                ))}</tr>
            </thead>
            <tbody>
              {data.rows.map((row) => (
                <tr key={row.team_id} className="border-t" data-testid={`week-row-${row.team_id}`}>
                  <td className="p-2"><div className="font-medium">{row.team_name}</div>
                    {row.pattern_name ? <div className="text-[11px] text-muted-foreground">{row.pattern_name} · V{(row.week_index ?? 0) + 1}/{row.cycle_weeks}</div>
                      : <div className="text-[11px] text-muted-foreground/60">Inget mönster</div>}</td>
                  {(row.days.length ? row.days : Array(7).fill(null)).map((d, i) => (
                    <td key={i} className="p-2 text-center">
                      {d && !d.off ? <span className="inline-block rounded bg-accent/10 px-2 py-0.5 text-xs text-accent">{d.shift_name || "Skift"}</span>
                        : <span className="text-xs text-muted-foreground/40">Ledig</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>}
    </Card>
  );
}
